import React, { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PaginatedEntityTable, ShareButton } from "components/common";
import { ButtonToolbar } from "components/bootstrap";
import { fetchPaginatedCollectionEntities, keyFn } from "collections/hooks/useCollectionEntities";
import EntityShareModal from "components/permissions/EntityShareModal";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import CollectionEntityActions from "./CollectionEntityActions";
import customColumnRenderers from "./customColumnRenderers";
import BulkActions from "./BulkActions";
export const DEFAULT_LAYOUT = {
  entityTableId: "collection_entities",
  defaultPageSize: 20,
  defaultSort: { attributeId: "name", direction: "asc" },
  defaultDisplayedAttributes: ["name", "type", "owners"],
  defaultColumnOrder: ["name", "type", "owners"]
};
const CollectionEntitiesList = ({ collectionId, collectionTitle }) => {
  const queryClient = useQueryClient();
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState(false);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const onBulkSelect = (selected, data) => {
    setSelectedEntities(data.filter((entity) => selected.includes(entity.id)));
  };
  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: onBulkSelect,
      actions: /* @__PURE__ */ React.createElement(BulkActions, { selectedEntities, collectionId })
    }),
    [collectionId, selectedEntities]
  );
  const refetchCollection = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY, collectionId]
    }),
    [queryClient, collectionId]
  );
  const handleShareClose = () => {
    refetchCollection();
    toggleCollectionContentShareModal();
  };
  const collectionEntitiesActions = useCallback(
    ({ id, name, type }) => /* @__PURE__ */ React.createElement(CollectionEntityActions, { type, collectionId, id, name }),
    [collectionId]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: "Collection Content",
      entityActions: collectionEntitiesActions,
      tableLayout: DEFAULT_LAYOUT,
      fetchEntities: (searchParams) => fetchPaginatedCollectionEntities(collectionId, searchParams),
      keyFn: (searchParams) => keyFn(collectionId, searchParams),
      columnRenderers: customColumnRenderers,
      entityAttributesAreCamelCase: false,
      searchPlaceholder: "Search for content",
      bulkSelection,
      topRightCol: /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
        ShareButton,
        {
          title: "Share Content",
          entityId: collectionId,
          entityType: "collection_entities",
          onClick: toggleCollectionContentShareModal
        }
      ))
    }
  ), showCollectionContentShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: collectionId,
      entityType: "collection_entities",
      entityTypeTitle: "Collection Content",
      entityTitle: collectionTitle,
      description: "Search for a User or Team to add as collaborator on this collection's content.",
      onClose: handleShareClose
    }
  ));
};
export default CollectionEntitiesList;
